import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import { api, getToken, setToken, setUnauthorizedHandler, type Me, type Permissions } from "./api";
import { MARKETS } from "./format";

// ---------------------------------------------------------------- session (who is signed in, what they may do)

interface SessionCtx {
  token: string | null;
  me: Me | null;
  loading: boolean;
  signIn: (token: string) => void;
  signOut: () => Promise<void>;
  /** perm held in market (or in any market when market is omitted/empty). Display only; admin-api enforces. */
  can: (perm: string, market?: string | null) => boolean;
  /** Markets where perm is held; null means all markets. */
  marketsFor: (perm: string) => string[] | null;
  expired: boolean;
}

const Ctx = createContext<SessionCtx | null>(null);

export function canWith(perms: Permissions | undefined, perm: string, market?: string | null): boolean {
  const m = perms?.[perm];
  if (!m) return false;
  if (!market) return true;
  return m.includes("*") || m.includes(market);
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [token, setTok] = useState<string | null>(getToken());
  const [expired, setExpired] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    // RequireSession sends the user to /sign-in?expired=1 once the token is gone.
    setUnauthorizedHandler(() => {
      setExpired(true);
      setTok(null);
      qc.clear();
    });
    return () => setUnauthorizedHandler(null);
  }, [qc, navigate]);

  const meQ = useQuery({ queryKey: ["me"], queryFn: api.me, enabled: !!token, staleTime: 60_000, refetchInterval: 5 * 60_000 });

  const signIn = useCallback(
    (t: string) => {
      setToken(t);
      setTok(t);
      setExpired(false);
      qc.clear();
    },
    [qc],
  );

  const signOut = useCallback(async () => {
    try {
      await api.logout();
    } catch {
      /* already signed out */
    }
    setToken(null);
    setTok(null);
    qc.clear();
    navigate("/sign-in", { replace: true });
  }, [qc, navigate]);

  const me = token ? (meQ.data ?? null) : null;
  const value = useMemo<SessionCtx>(
    () => ({
      token,
      me,
      loading: !!token && meQ.isLoading,
      signIn,
      signOut,
      expired,
      can: (perm, market) => canWith(me?.permissions, perm, market),
      marketsFor: (perm) => {
        const m = me?.permissions?.[perm];
        if (!m) return [];
        return m.includes("*") ? null : m;
      },
    }),
    [token, me, meQ.isLoading, signIn, signOut, expired],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error("useSession outside SessionProvider");
  return c;
}

// ---------------------------------------------------------------- market filter (global, top bar)

interface MarketCtx {
  /** "" = all markets in scope */
  market: string;
  setMarket: (m: string) => void;
  /** Markets the staff member can see anything in. */
  markets: string[];
  /** The selected market, or the first in scope (for pages that need exactly one). */
  one: string;
}

const MCtx = createContext<MarketCtx | null>(null);
const MARKET_KEY = "softlife.admin.market";

export function MarketProvider({ children }: { children: ReactNode }) {
  const { me } = useSession();
  const [market, setM] = useState<string>(() => {
    try {
      return sessionStorage.getItem(MARKET_KEY) || "";
    } catch {
      return "";
    }
  });
  const markets = useMemo((): string[] => {
    if (!me) return [...MARKETS];
    const set = new Set<string>();
    for (const scope of Object.values(me.permissions)) {
      if (scope.includes("*")) return [...MARKETS];
      scope.forEach((m) => set.add(m));
    }
    return MARKETS.filter((m) => set.has(m));
  }, [me]);
  const setMarket = useCallback((m: string) => {
    setM(m);
    try {
      sessionStorage.setItem(MARKET_KEY, m);
    } catch {
      /* ignore */
    }
  }, []);
  const effective = market && markets.includes(market) ? market : "";
  const value = useMemo(() => ({ market: effective, setMarket, markets, one: effective || markets[0] || "NG" }), [effective, setMarket, markets]);
  return <MCtx.Provider value={value}>{children}</MCtx.Provider>;
}

export function useMarket(): MarketCtx {
  const c = useContext(MCtx);
  if (!c) throw new Error("useMarket outside MarketProvider");
  return c;
}

// ---------------------------------------------------------------- catalogue (play symbol, pack 2) per market

/**
 * The active catalogue for a market, when the staff member can read the economy.
 * Used only for display (play symbol, pack prices); never for decisions.
 */
export function useCatalogue(market: string | null | undefined) {
  const { can } = useSession();
  const allowed = !!market && can("economy.read", market);
  return useQuery({
    queryKey: ["catalogue-active", market],
    enabled: allowed,
    staleTime: 10 * 60_000,
    retry: false,
    queryFn: async () => {
      const { versions } = await api.catVersions(market!);
      const active = versions.find((v) => v.status === "active");
      if (!active) return null;
      const v = await api.catVersion(market!, active.version);
      return { version: active.version, body: v.body };
    },
  });
}

export interface WorldCatalogue {
  cities: { key: string; name: string; home?: boolean }[];
  districts: { key: string; city: string; name: string; kind?: string; activities?: string[] }[];
  landmarks: { key: string; city: string; district?: string; name: string }[];
  interiors: { key: string; name: string; kind?: string }[];
  /** The slots brands can rent as employer:<key> are the brandable ones. */
  workplaces?: { key: string; city: string; district?: string; kind?: string; employer?: string; brandable?: boolean }[] | null;
  /** The micro-courses a market has (course_sponsor:<key>). */
  courses?: { key: string; name: string }[] | null;
}

/**
 * The world section (cities, districts, landmarks) of a market's active
 * catalogue, for pickers. null when the viewer can't read the catalogue
 * (pickers then fall back to free text; admin-api validates either way).
 */
export function useWorldCatalogue(market: string | null | undefined): WorldCatalogue | null {
  const q = useCatalogue(market);
  const w = (q.data?.body as { world?: Partial<WorldCatalogue> } | undefined)?.world;
  if (!w) return null;
  return { cities: w.cities ?? [], districts: w.districts ?? [], landmarks: w.landmarks ?? [], interiors: w.interiors ?? [], workplaces: w.workplaces, courses: w.courses };
}

export function usePlaySymbol(market: string | null | undefined): string | undefined {
  const q = useCatalogue(market);
  return q.data?.body.play_symbol || undefined;
}

/** Play symbols for every market in scope (for lists that mix markets). */
export function usePlaySymbols(): Record<string, string> {
  const { markets } = useMarket();
  const { can } = useSession();
  const results = useQueries({
    queries: markets.map((m) => ({
      queryKey: ["catalogue-active", m],
      enabled: can("economy.read", m),
      staleTime: 10 * 60_000,
      retry: false,
      queryFn: async () => {
        const { versions } = await api.catVersions(m);
        const active = versions.find((v) => v.status === "active");
        if (!active) return null;
        const v = await api.catVersion(m, active.version);
        return { version: active.version, body: v.body };
      },
    })),
  });
  const out: Record<string, string> = {};
  results.forEach((r, i) => {
    const sym = r.data?.body.play_symbol;
    if (sym) out[markets[i]] = sym;
  });
  return out;
}

// ---------------------------------------------------------------- theme

type Theme = "light" | "dark" | "system";
const THEME_KEY = "softlife.admin.theme";

function applyTheme(t: Theme) {
  const dark = t === "dark" || (t === "system" && window.matchMedia?.("(prefers-color-scheme: dark)").matches);
  document.documentElement.dataset.theme = dark ? "dark" : "light";
}

export function useTheme(): [Theme, (t: Theme) => void] {
  const [theme, setTheme] = useState<Theme>(() => {
    try {
      return (localStorage.getItem(THEME_KEY) as Theme) || "system";
    } catch {
      return "system";
    }
  });
  useEffect(() => {
    applyTheme(theme);
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      /* ignore */
    }
    if (theme !== "system") return;
    const mq = window.matchMedia?.("(prefers-color-scheme: dark)");
    const on = () => applyTheme("system");
    mq?.addEventListener?.("change", on);
    return () => mq?.removeEventListener?.("change", on);
  }, [theme]);
  return [theme, setTheme];
}
